import {
	createFileRoute,
	Outlet,
	useMatch,
	useParams,
} from "@tanstack/react-router";
import { Plus } from "@untitledui/icons";
import { useState } from "react";
import { z } from "zod";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { OrgAssetsTable } from "@/features/assets/components";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useCreatableSubOrganizations } from "@/hooks/use-creatable-sub-organizations";

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/assets",
)({
	component: AssetsPage,
	validateSearch: (search) =>
		z
			.object({
				q: z.string().optional(),
			})
			.parse(search),
});

function AssetsPage() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/assets",
	});
	const workspaceMatch = useMatch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/assets/(asset)/$assetId/workspace",
		shouldThrow: false,
	});
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);

	const canCreateAssets = useCreatableSubOrganizations({
		actionName: "CreateAsset",
		organizationId: params.orgId,
		entityType: "Asset",
	});

	// Workspace is a full second-level page — skip landing chrome.
	if (workspaceMatch) {
		return <Outlet />;
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("main.nav.items.assets.label")}
						description={t("main.assets.table.description")}
						actions={
							canCreateAssets.canCreate
								? [
										{
											label: t("common.actions.add"),
											icon: Plus,
											color: "accent",
											onPress: () => {
												setIsCreateOpen(true);
											},
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<OrgAssetsTable
					orgId={params.orgId}
					tableOptionsPortalTarget={tableOptionsHost}
					isCreateOpen={isCreateOpen}
					onCreateOpenChange={setIsCreateOpen}
				/>
			</PageLayout>
			{/* Drawer / entity child routes mount here */}
			<Outlet />
		</>
	);
}
